/**
 * Service worker registration helper.
 * Called from main.tsx in production only (import.meta.env.PROD).
 *
 * The SW lives at /sw.js (Vite output mapping in vite.config.ts) so its
 * scope is the entire origin.
 */

export interface RegisterOptions {
  /** Fired when an update is installed and waiting for activation. */
  onUpdateAvailable?: (applyUpdate: () => void) => void
  /** Fired on the first install (no previous SW was controlling the page). */
  onOfflineReady?:    () => void
}

export async function registerServiceWorker(
  opts: RegisterOptions = {},
): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null

  const registration = await navigator.serviceWorker.register('/sw.js', { type: 'module' })

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (!installing) return

    installing.addEventListener('statechange', () => {
      if (installing.state !== 'installed') return

      if (navigator.serviceWorker.controller) {
        // An older SW already controls this page → this is an update.
        opts.onUpdateAvailable?.(() => {
          installing.postMessage({ type: 'SKIP_WAITING' })
        })
      } else {
        // Very first install on this client.
        opts.onOfflineReady?.()
      }
    })
  })

  // When the new SW takes control, reload exactly once so it serves the page.
  let reloadGuard = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadGuard) return
    reloadGuard = true
    window.location.reload()
  })

  return registration
}

export async function unregisterServiceWorker(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return false
  return reg.unregister()
}
